import { useState } from 'react';
import { Card, CardBody } from '@components/ui';
import {
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  DocumentMagnifyingGlassIcon,
  PlusCircleIcon,
  UserGroupIcon,
  CubeIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  BuildingStorefrontIcon,
  UsersIcon,
  LifebuoyIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@lib/utils/cn';

interface ManualSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: ManualContent[];
}

interface ManualContent {
  subtitle?: string;
  text?: string;
  list?: string[];
  image?: string;
  imageAlt?: string;
  note?: string;
}

const manualData: ManualSection[] = [
  {
    id: 'acceso',
    title: '1. Acceso al Portal',
    icon: <ArrowRightOnRectangleIcon className="h-6 w-6" />,
    content: [
      {
        subtitle: 'Inicio de sesión',
        text: 'Desde la pantalla de inicio de sesión ingresas al portal con tus credenciales.',
        list: [
          'Correo Electrónico: el correo con el que fue creado tu usuario.',
          'Contraseña: tu clave de acceso.',
          'Recordarme: mantiene tu sesión iniciada en este dispositivo.',
          '¿Olvidaste tu contraseña?: envía un enlace a tu correo para restablecerla.',
        ],
        note: 'Si tu empresa usa cuenta corporativa de Microsoft, verás además el botón "Iniciar sesión con Microsoft".',
      },
      {
        subtitle: 'Selección de empresa',
        text: 'Si tu usuario tiene acceso a más de una empresa, luego de iniciar sesión el sistema te pide seleccionar con cuál deseas trabajar. Puedes buscar por nombre y alternar entre vista de tarjetas o de lista. Si solo tienes una empresa, el sistema la selecciona automáticamente.',
      },
      {
        subtitle: 'Barra lateral de navegación',
        text: 'Una vez dentro, el menú lateral izquierdo agrupa todas las secciones del portal:',
        image: '/images/manual/crop-sidebar.png',
        imageAlt: 'Menú lateral de navegación',
        list: [
          'Principal: Dashboard, Facturas Activas, Archivo Histórico, Nueva Factura, Clientes, Productos, Reportes.',
          'Empresa: Mi Empresa, Configuración.',
          'Administración: Sucursales, Gestión de Usuarios.',
          'Soporte: Manual de Usuario, Preguntas Frecuentes.',
        ],
        note: 'El menú se adapta a tu rol: los usuarios vendedores solo ven Facturas Activas, Nueva Factura, Clientes, Productos y Soporte. Las secciones administrativas (Dashboard, Reportes, Configuración, etc.) están disponibles para administradores.',
      },
      {
        subtitle: 'Cambio de empresa y cierre de sesión',
        text: 'En la barra superior, el selector de empresa te permite cambiar de compañía en cualquier momento sin cerrar sesión. El menú de usuario (tus iniciales, arriba a la derecha) contiene la opción "Cerrar Sesión".',
      },
    ],
  },
  {
    id: 'dashboard',
    title: '2. Dashboard',
    icon: <HomeIcon className="h-6 w-6" />,
    content: [
      {
        text: 'El Dashboard es un resumen general de tu actividad de facturación electrónica para el rango de fechas que elijas.',
        image: '/images/manual/dashboard.png',
        imageAlt: 'Vista general del Dashboard',
        note: 'Disponible para administradores.',
      },
      {
        subtitle: 'Filtro de fechas y actualización',
        text: 'En la parte superior defines el período a analizar.',
        image: '/images/manual/crop-dash-fechas.png',
        imageAlt: 'Filtro de fechas y botón Actualizar',
        list: [
          'Fecha Inicio y Fecha Fin: por defecto van del primer día del mes hasta hoy.',
          'Botón Actualizar: aplica el rango seleccionado y recalcula todas las tarjetas, gráficas y tablas. Siempre que cambies las fechas debes presionarlo.',
        ],
      },
      {
        subtitle: 'Tarjetas de indicadores',
        text: 'Ocho tarjetas muestran los números principales del negocio, cada una con su variación porcentual respecto al período anterior:',
        image: '/images/manual/crop-dash-tarjetas.png',
        imageAlt: 'Tarjetas de indicadores del Dashboard',
        list: [
          'Total Facturas: cantidad total emitida en el período.',
          'Facturas Procesadas: la DGI la recibió y la registró correctamente.',
          'Facturas con error: la DGI rechazó el documento: no quedó registrado como factura electrónica válida.',
          'Facturas Autorizado uso de la FE: la factura fue autorizada para circular/usarse, pero la DGI todavía no confirmó el registro definitivo.',
          'Facturas Anuladas: una factura que se emitió y quedó registrada correctamente en la DGI, y después el emisor la canceló.',
          'Ingresos del Período: monto total facturado.',
          'Promedio por Factura: monto promedio de cada documento.',
          'APROBADAS VS RECHAZADAS: cantidad de facturas aceptadas frente a las rechazadas en el período.'
        ],
      },
      {
        subtitle: 'Resumen adicional',
        text: 'Tres tarjetas complementarias:',
        list: [
          'Tasa de Aprobación: porcentaje de facturas aprobadas respecto al total.',
          'Tiempo Promedio de Aprobación: horas que tarda la DGI en aprobar desde la emisión.',
          'Facturación Proyectada: estimación de cierre del período si mantienes el mismo ritmo.',
        ],
      },
      {
        subtitle: 'Gráfica: Facturación Mensual',
        text: 'Gráfica de barras con la cantidad de facturas de los últimos 6 meses. Te ayuda a visualizar el crecimiento, detectar meses de mayor o menor actividad y comparar tu desempeño mensual.',
        image: '/images/manual/crop-dash-grafica-mensual.png',
        imageAlt: 'Gráfica de Facturación Mensual',
      },
      {
        subtitle: 'Gráfica: Distribución por Estado',
        text: 'Gráfica circular que muestra el estado real de las facturas del período seleccionado.',
        image: '/images/manual/crop-dash-grafica-estado.png',
        imageAlt: 'Gráfica de Distribución por Estado',
        list: [
          'Procesada: facturas aceptadas correctamente.',
          'Autorizado uso de la FE: facturas autorizadas para circular/usarse.',
          'Error: facturas con errores en la información.',
        ],
      },
      {
        subtitle: 'Tipos diferentes · Top 5 por tipo',
        text: 'Gráfica circular que muestra los Tipos de Documento',
        image: '/images/manual/crop-dash-top5-clientes.png',
        imageAlt: 'Gráfica de Distribución por Tipo de Documento',
        list: [
          'Top 5 Tipos de Documento: Tipo (código), Cantidad, Total y Promedio.',
        ],
      },
    ],
  },
  {
    id: 'facturas-activas',
    title: '3. Facturas Activas',
    icon: <DocumentTextIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Desde esta sección consultas, buscas y ves el detalle de las facturas emitidas por tu empresa, y puedes iniciar la emisión de una nueva con el botón Emitir Factura.',
        image: '/images/manual/facturas-activas.png',
        imageAlt: 'Pantalla de Facturas Activas',
      },
      {
        subtitle: 'Búsqueda por CUFE',
        text: 'El campo principal busca directamente por el CUFE (Código Único de Factura Electrónica). El botón "Más filtros" despliega los filtros avanzados. El check de "Ocultar facturas con error", permite visualizar u ocultar facturas con errores.',
        image: '/images/manual/crop-facturas-busqueda.png',
        imageAlt: 'Búsqueda por CUFE y Más filtros',
      },
      {
        subtitle: 'Filtros avanzados',
        image: '/images/manual/crop-facturas-filtros.png',
        imageAlt: 'Panel de filtros avanzados',
        list: [
          'Tipo de Documento: los 10 tipos de la DGI (factura, notas de crédito/débito, exportación, etc.).',
          'Estado de la Factura: Todos, Procesada, Error, Autorizado uso de la FE, Procesando, Pendiente o Anulada.',
          'Nombre del Cliente, RUC/Cédula.',
          'Código de tienda y Punto de venta.',
          'Fecha Inicio y Fecha Fin.',
        ],
      },
      {
        subtitle: 'Tabla de facturas',
        text: 'Cada fila es una factura emitida. Las columnas muestran:',
        image: '/images/manual/crop-facturas-tabla.png',
        imageAlt: 'Tabla de facturas emitidas',
        list: [
          'N° de documento (con una etiqueta de color según el tipo).',
          'Cufe, Nombre del cliente, Ruc/Cédula.',
          'N° de tienda y Punto de venta.',
          'Estado: insignia de color; al hacer clic abre el "Detalle del estado" con el código de respuesta y la resolución de la DGI.',
          'Resolución, Monto total y Fecha de emisión.',
          'Ver detalles: abre la factura completa.',
        ],
      },
    ],
  },
  {
    id: 'archivo-historico',
    title: '4. Archivo Histórico',
    icon: <ArchiveBoxIcon className="h-6 w-6" />,
    content: [
      {
        text: 'En esta pantalla consultas las facturas con más de 90 días de antigüedad.',
        image: '/images/manual/archivo-historico.png',
        imageAlt: 'Pantalla de Archivo Histórico',
        note: 'Se muestran únicamente los registros mayores a 90 días. La fecha fin de la búsqueda tiene como tope hoy menos 90 días.',
      },
      {
        subtitle: 'Filtros de búsqueda',
        text: 'La opcion "Más filtros" permite encontrar documentos específicos por los siguientes campos:',
        image: '/images/manual/archivo-historico-filtros.png',
        imageAlt: 'Pantalla de Archivo Histórico - Filtros',
        list: [
          'Tipo de Documento: los 10 tipos de la DGI.',
          'Estado de la Factura: Procesada, Error, Autorizado, Procesando, Pendiente o Anulada.',
          'Nombre del Cliente y RUC del Cliente.',
          'Código de tienda y Punto de venta.',
          'Número de Documento.',
          'Fecha inicio y Fecha fin.',
        ],
      },
      {
        subtitle: 'Resultados',
        text: 'La tabla muestra # de Documento, Tipo de Documento, Cliente, RUC, Monto, Fecha, Estado (con detalle) y la acción Ver para abrir el documento. Es posible filtrar los registros utilizando el campo de búsqueda para cualquiera de los parámetros indicados.  El check de "Ocultar facturas con error", permite visualizar u ocultar facturas con errores.',
        image: '/images/manual/archivo-historico-buscador.png',
        imageAlt: 'Pantalla de Archivo Histórico - Buscador',
      },
    ],
  },
  {
    id: 'detalle-factura',
    title: '5. Detalle de Factura',
    icon: <DocumentMagnifyingGlassIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Al abrir una factura ves toda su información y las acciones disponibles según su estado. Se accede desde "Ver detalles" en Facturas Activas o en el Archivo Histórico.',
        image: '/images/manual/detalle-factura.png',
        imageAlt: 'Detalle de una factura',
      },
      {
        subtitle: 'Información del documento',
        list: [
          'Información del Emisor: nombre, ubicación, RUC y DV, sucursal y teléfono.',
          'Información del Receptor: nombre, ubicación, RUC/DV, correo y teléfono.',
          'Lista de Productos: código, descripción, cantidad, precio unitario, descuento, ITBMS, ISC, OTI y total por línea.',
          'Formas de Pago y Totales: subtotal, descuentos, impuestos y total.',
          'Información General (panel derecho): código QR, estado del documento. Luego opciones para Reenviar Email, Imprimir CAFE, Ver XML y Ver en DGI. Por último datos como Tipo de documento, N° de documento, CUFE, Protocolo de autorización y Fecha de emisión.',
        ],
      },
      {
        subtitle: 'Menú de Acciones',
        text: 'El botón Acciones (arriba a la derecha) cambia según el estado de la factura:',
        image: '/images/manual/crop-detalle-acciones.png',
        imageAlt: 'Menú de Acciones del detalle de factura',
        list: [
          'Hacer corrección (mismo CUFE): se ajusta la información y se re-emite el mismo documento.',
          'Hacer corrección (nuevo CUFE): se ajusta la información y se emite nuevo documento .',
          'Reenviar Email: reenvía la factura al cliente.',
          'Anular Factura: disponible dentro de los 7 días posteriores a su creación.',
          'Nota de Crédito y Nota de Débito: emite una nota asociada a esta factura.',
          'Imprimir CAFE y Ver XML: obtén el comprobante o el archivo XML.',
          'Clonar Factura: crea una nueva factura tomando esta como base.',
        ],
        note: 'Si la factura quedó con error, el menú ofrece "Hacer corrección" (con el mismo CUFE o con uno nuevo) para reemitirla.',
      },
    ],
  },
  {
    id: 'nueva-factura',
    title: '6. Nueva Factura (Asistente)',
    icon: <PlusCircleIcon className="h-6 w-6" />,
    content: [
      {
        text: 'La emisión de una factura electrónica se hace mediante un asistente paso a paso que cumple los requisitos de la DGI. Una barra de progreso en la parte superior indica en qué paso estás.',
        image: '/images/manual/nueva-factura.png',
        imageAlt: 'Asistente de Nueva Factura (paso 1)',
        note: 'Una factura normal tiene 5 pasos. Las notas de crédito y débito tienen 6, ya que agregan un paso de "Referencia" al documento original.',
      },
      {
        subtitle: 'Paso 1 — Cliente (Emisor y Receptor)',
        text: 'Se define quién emite y quién recibe la factura.',
        list: [
          'Emisor: datos de la empresa (nombre, código, RUC y DV) que se cargan automáticamente. Debes elegir la Sucursal a utilizar y el Código del punto de facturación.',
          'Receptor: Nombre de receptor, Tipo de receptor (Contribuyente, Consumidor Final, Gobierno o Extranjero), Tipo RUC (Natural Jurídico), Tipo doc, RUC, DV, Correo electrónico (obligatorio), Teléfono  y Dirección.',
        ],
        note: 'Con el botón "+" junto al receptor puedes buscar un cliente ya registrado, y junto a la sucursal puedes crear una nueva sin salir del asistente.',
      },
      {
        subtitle: 'Paso Referencia (solo notas de crédito/débito)',
        text: 'Muestra los datos del documento electrónico referenciado (número de factura interna, fecha, número en papel si aplica y el CUFE). La nota hereda automáticamente el cliente y los items de la factura original.',
      },
      {
        subtitle: 'Paso 2 — Detalles de la Factura',
        text: 'Se configuran los datos fiscales del documento:',
        image: '/images/manual/wizard-2-detalles.png',
        imageAlt: 'Paso 2 — Detalles de la Factura',
        list: [
          'Identificación del documento: Número de Factura (Automático o Manual), Tipo de Documento, Naturaleza de la Operación y Tipo de Transacción.',
          'Tipo de Operación: Salida (venta) o Entrada (compra) y Destino de la Operación (Panamá o Extranjero).',
          'Fechas: Fecha de Emisión y Fecha de Vencimiento.',
          'Configuración de Envío: Forma de Generación del Documento (Sin generación de CAFE, Cinta de papel o Papel formato carta), Envío del Contenedor (Normal, El receptor exceptúa al emisor de la obligatoriedad de envío del contenedor).',
        ],
      },
      {
        subtitle: 'Paso 3 — Items (productos y servicios)',
        text: 'Se agregan las líneas de la factura con el botón Agregar Item. Cada item puede tomarse del catálogo o crearse en el momento.',
        image: '/images/manual/wizard-3-items.png',
        imageAlt: 'Paso 3 — Items de la factura',
        list: [
          'Datos del item: referencia, código, nombre, descripción, cantidad, unidad, precio unitario y descuento.',
          'Impuestos por item: ITBMS (0/7/10/15%), ISC y OTI.',
          'La tabla muestra cada línea y una tarjeta de totales (Subtotal, Descuentos, Impuestos y Total).',
        ],
      },
      {
        subtitle: 'Paso 4 — Notas (información adicional y pago)',
        text: 'Se completan condiciones de pago e información complementaria:',
        image: '/images/manual/wizard-4-notas.png',
        imageAlt: 'Paso 4 — Información adicional y pago',
        list: [
          'Configuración general: moneda, retención, información de pedido.',
          'Tiempo de pago: contado, crédito (a plazo) o mixto.',
          'Acarreo y seguro globales: acarreo y seguro globales.',
          'Formas de Pago: se agregan uno o varios métodos con su monto.',
          'Descuentos globales: descripcion y monto globales.',
          'Notas: texto libre que aparecerá en el CAFE.',
          'Resumen de Totales: subtotal, descuentos, impuestos, acarreos, seguros y total.',
        ],
      },
      {
        subtitle: 'Paso 5 — Revisar y Emitir',
        text: 'Muestra un resumen completo de emisor, sucursal, cliente, detalles, items, totales, formas de pago y notas. Desde aquí puedes ver una Vista Previa del documento y, al confirmar con Emitir Factura, el sistema la envía a la DGI y muestra el resultado (éxito o error con su detalle).',
        image: '/images/manual/wizard-5-revisar.png',
        imageAlt: 'Paso 5 — Revisar y emitir la factura',
      },
    ],
  },
  {
    id: 'clientes',
    title: '7. Clientes',
    icon: <UserGroupIcon className="h-6 w-6" />,
    content: [
      {
        text: 'En esta sección administras el directorio de clientes a los que emites facturas. Puedes crearlos manualmente o cargarlos masivamente con Importar Excel.',
        image: '/images/manual/clientes.png',
        imageAlt: 'Listado de Clientes',
      },
      {
        subtitle: 'Búsqueda y tabla',
        text: 'El buscador detecta automáticamente si escribes un nombre o un RUC (mínimo 3 caracteres). La tabla muestra Cliente RUC (con DV), Tipo de Cliente (Particular o Comercial), Contacto (Email, Teléfono) y Fecha de Creación, con acciones para editar o eliminar cada cliente.',
      },
      {
        subtitle: 'Nuevo / Editar Cliente Particular',
        text: 'El formulario se divide en dos columnas: "Identidad y contacto" y "Direcciones".',
        image: '/images/manual/nuevo-cliente-particular.png',
        imageAlt: 'Formulario de Nuevo Cliente Particular',
        list: [
          'Identidad y contacto: Nombre Completo, Número de Documento (Cedula/Pasaporte), DV, Tipo de Receptor DGI, Género, Nacionalidad, Email (obligatorio) y Teléfono.',
          'Verificar con la DGI: valida la identificación, DV y Tipo de Receptor DGI.',
          'Direcciones: puedes agregar varias; para Panamá se seleccionan Provincia, Distrito, Corregimiento y Barrio en cascada y el Código de Ubicación se calcula solo.',
        ],
      },
      {
        subtitle: 'Nuevo / Editar Cliente Comercial',
        text: 'El formulario se divide en dos columnas: "Identidad y contacto" y "Direcciones".',
        image: '/images/manual/nuevo-cliente-comercial.png',
        imageAlt: 'Formulario de Nuevo Cliente Comercial',
        list: [
          'Identidad y contacto: Razón Social, Nombre Comercial, RUC, DV, Tipo de Receptor DGI, País de Operación, Email (obligatorio) y Teléfono.',
          'Verificar RUC con DGI: valida el RUC.',
          'Direcciones: puedes agregar varias; para Panamá se seleccionan Provincia, Distrito, Corregimiento y Barrio en cascada y el Código de Ubicación se calcula solo.',
        ],
      },
    ],
  },
  {
    id: 'productos',
    title: '8. Productos',
    icon: <CubeIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Catálogo donde gestionas los productos y servicios que usarás al facturar. También admite carga masiva con Importar Excel.',
        image: '/images/manual/productos.png',
        imageAlt: 'Catálogo de Productos',
      },
      {
        subtitle: 'Búsqueda y tabla',
        text: 'El buscador alterna entre Nombre y Código (mínimo 2 caracteres). La tabla muestra SKU, Nombre (con descripción), Tipo (Producto o Servicio), Categoría, Precio, Código de impuesto y Estado (Activo/Inactivo), con acciones para editar o eliminar.',
      },
      {
        subtitle: 'Nuevo / Editar Producto',
        text: 'Formulario para registrar un producto o servicio.',
        image: '/images/manual/nuevo-producto.png',
        imageAlt: 'Formulario de Nuevo Producto',
        list: [
          'Código (SKU), Categoría (Servicios, Productos, Software, Consultoría, Hardware u Otro) y Nombre del Producto.',
          'Descripción (máx. 500 caracteres).',
          'Precio, Impuesto (0/7/10/15%), Unidad de medida y Stock (solo para productos físicos).',
          'Segmento y Familia (clasificación UNSPSC, opcional).',
          'Producto Activo: define si está disponible para usarse en facturas.',
        ],
      },
    ],
  },
  {
    id: 'reportes',
    title: '9. Reportes',
    icon: <ChartBarIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Genera reportes en Excel o PDF con la información de tus facturas.',
        image: '/images/manual/reportes.png',
        imageAlt: 'Administrador de reportes',
        note: 'Disponible para administradores.',
      },
      {
        subtitle: '1. Documentos emitidos (Excel)',
        text: 'Archivo Excel con las facturas emitidas correctamente en una fecha específica. Selecciona la fecha y presiona Generar reporte.',
      },
      {
        subtitle: '2. Reporte Z (PDF)',
        text: 'PDF con el total de facturas emitidas en un rango de fechas. Fecha inicio y fin, con la opción de Filtro por sucursales para limitar el resultado a una o varias.',
      },
      {
        subtitle: '3. Balance General (Excel)',
        text: 'Excel con todas las facturas registradas en un rango de tiempo, también con filtro opcional por sucursales.',
      },
    ],
  },
  {
    id: 'mi-empresa',
    title: '10. Mi Empresa',
    icon: <BuildingOfficeIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Aquí actualizas la información legal, visual y de contacto de tu empresa dentro del sistema.',
        image: '/images/manual/mi-empresa.png',
        imageAlt: 'Editar Empresa',
        note: 'Disponible para administradores.',
      },
      {
        subtitle: 'Logo de la Empresa',
        list: [
          'Seleccionar imagen para cargar o cambiar el logo.',
          'Formatos permitidos: JPG y PNG.',
          'Tamaño máximo: 1MB.',
        ],
      },
      {
        subtitle: 'Información Legal',
        list: [
          'Tipo de Contribuyente: Persona Natural o Jurídica.',
          'RUC y DV.',
          'Razón Social y Nombre Comercial.',
        ],
      },
      {
        subtitle: 'Información de Contacto',
        list: [
          'Email (solo lectura).',
          'Teléfono.',
          'País y Dirección.',
        ],
        note: 'Los administradores con permiso pueden además crear una nueva compañía desde el botón "Nueva compañía".',
      },
    ],
  },
  {
    id: 'configuracion',
    title: '11. Configuración',
    icon: <Cog6ToothIcon className="h-6 w-6" />,
    content: [
      {
        text: 'La Configuración Administrativa se organiza en tres pestañas: Configuración General, Información Fiscal y Certificados, y Templates.',
        image: '/images/manual/crop-config-tabs.png',
        imageAlt: 'Pestañas de Configuración',
        note: 'Disponible para administradores.',
      },
      {
        subtitle: 'Configuración General',
        image: '/images/manual/configuracion.png',
        imageAlt: 'Pestaña de Configuración General',
        list: [
          'Prefijo de Facturas: texto que antecede al número (ej. FE-00001).',
          'Tasa de Impuesto por Defecto (%): se aplica automáticamente al crear facturas.',
          'Auto-aprobar Facturas: aprueba sin revisión manual.',
          'Notificaciones por email y Autenticación de dos factores (2FA).',
        ],
      },
      {
        subtitle: 'Información Fiscal y Certificados',
        text: 'Muestra los datos fiscales de la compañía (Razón Social, Nombre Comercial, RUC, DV, Tipo de Contribuyente etc.) y el estado de los certificados digitales usados para firmar y autenticar ante la DGI.',
        image: '/images/manual/config-fiscal.png',
        imageAlt: 'Información Fiscal y Certificados',
        list: [
          'Certificado de Autenticación: valida la conexión con los servicios de la DGI.',
          'Certificado de Firma: firma digitalmente cada factura.',
          'Certificate Key (Password): contraseña obligatoria para validar los certificados.',
          'Fechas: Fecha de Creación, Fecha de Vencimiento y Última Modificación.',
        ],
        note: 'Cuando los certificados están configurados correctamente, aparece una insignia que lo confirma y el sistema queda listo para emitir.',
      },
      {
        subtitle: 'Templates (Email y CAFE)',
        text: 'Personaliza los correos que se envían al cliente junto con la factura y la visualización del CAFE.',
        image: '/images/manual/config-templates.png',
        imageAlt: 'Configuración de Templates',
        list: [
          'General: Envío Automático de Email y Descripción del Template.',
          'Email: remitente, asunto, título y pie del correo.',
          'Colores: colores del correo con vista previa y logo del comprobante.',
          'Comprobantes CAFE: selección y configuración de la plantilla del CAFE.',
        ],
      },
    ],
  },
  {
    id: 'sucursales',
    title: '12. Gestión de Sucursales',
    icon: <BuildingStorefrontIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Administra las sucursales registradas de tu empresa.',
        image: '/images/manual/sucursales.png',
        imageAlt: 'Gestión de Sucursales',
        note: 'Disponible para administradores.',
      },
      {
        subtitle: 'Listado de sucursales',
        text: 'La tabla muestra Código (y código fiscal), Nombre, Ubicación (dirección y coordenadas), Contacto, Tipo de dirección, Tipo (Principal o Sucursal) y Estado (Activa o Inactiva). Puedes buscar por nombre o código y editar o desactivar cada sucursal.',
      },
      {
        subtitle: 'Nueva Sucursal',
        text: 'El botón Nueva Sucursal abre un asistente de 2 pasos.',
        image: '/images/manual/nueva-sucursal.png',
        imageAlt: 'Asistente de Nueva Sucursal',
        list: [
          'Paso 1 — Ubicación: selecciona el punto de la sucursal en el mapa; el sistema obtiene la dirección automáticamente.',
          'Paso 2 — Detalles: Provincia, Distrito y Corregimiento en cascada, detalle de la dirección, Código de Ubicación Fiscal (automático), y los datos de la sucursal: Código (4 dígitos), Descripción, Email y si está activa.',
        ],
      },
    ],
  },
  {
    id: 'usuarios',
    title: '13. Gestión de Usuarios',
    icon: <UsersIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Administra los usuarios que tienen acceso al sistema de facturación.',
        image: '/images/manual/usuarios.png',
        imageAlt: 'Gestión de Usuarios',
        note: 'Disponible para administradores.',
      },
      {
        subtitle: 'Listado de usuarios',
        text: 'La tabla muestra Nombre, Email, Teléfono, Compañías y Roles asignados, y Estado. Desde las acciones puedes editar, enviar la invitación por correo, y activar o desactivar un usuario. El buscador filtra por nombre o correo (mínimo 3 caracteres).',
      },
      {
        subtitle: 'Nuevo Usuario',
        text: 'El formulario crea un usuario y le asigna acceso.',
        image: '/images/manual/nuevo-usuario.png',
        imageAlt: 'Formulario de Nuevo Usuario',
        list: [
          'Compañías: una o varias a las que tendrá acceso.',
          'Información Personal: Nombre, Apellido, número de cédula y Email.',
          'Roles: Vendedor, Cliente B2B, Administrador de Compañía o Administrador.'
        ],
        note: 'Al crear el usuario, el sistema le envía un correo para que configure su contraseña.',
      },
    ],
  },
  {
    id: 'soporte',
    title: '14. Soporte',
    icon: <LifebuoyIcon className="h-6 w-6" />,
    content: [
      {
        subtitle: 'Manual de Usuario',
        text: 'Esta misma guía, disponible en cualquier momento desde el menú Soporte. Usa el buscador de la parte superior para encontrar rápidamente una sección.',
      },
      {
        subtitle: 'Preguntas Frecuentes',
        text: 'La sección de Preguntas Frecuentes reúne respuestas sobre firma electrónica, documento de apoderado, facturación y DGI, uso del sistema, planes y soporte. Es el primer lugar para resolver dudas comunes.',
      },
    ],
  },
];

// Image Modal Component
function ImageModal({
  isOpen,
  onClose,
  imageSrc,
  imageAlt,
  testIdBase,
}: {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  imageAlt: string;
  testIdBase?: string;
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a2233]/80 p-4 backdrop-blur-[3px]"
      onClick={onClose}
      data-testid={testIdBase ? `${testIdBase}-image-modal` : undefined}
    >
      <button
        onClick={onClose}
        data-testid={testIdBase ? `${testIdBase}-image-modal-close-button` : undefined}
        aria-label="Cerrar"
        className="absolute right-5 top-5 inline-flex h-[34px] w-[34px] items-center justify-center rounded-[10px] border border-white/20 bg-white/10 text-white transition-colors hover:border-white hover:bg-white hover:text-ink-850"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
      <img
        src={imageSrc}
        alt={imageAlt}
        className="max-h-[90vh] max-w-full rounded-[20px] object-contain shadow-[0_40px_100px_-20px_rgba(0,0,0,.6)]"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

function ManualContentItem({ content, testIdBase }: { content: ManualContent; testIdBase?: string }) {
  const [imageModalOpen, setImageModalOpen] = useState(false);

  return (
    <div className="space-y-4">
      {content.subtitle && (
        <h4 className="text-lg font-semibold text-secondary-800 dark:text-secondary-100 border-l-4 border-primary-500 pl-3">
          {content.subtitle}
        </h4>
      )}

      {content.text && (
        <p className="text-secondary-600 dark:text-secondary-400 leading-relaxed">
          {content.text}
        </p>
      )}

      {content.list && (
        <ul className="space-y-2 ml-1">
          {content.list.map((item, index) => (
            <li key={index} className="flex items-start gap-3 text-secondary-600 dark:text-secondary-400">
              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500 mt-2" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      {content.note && (
        <div className="flex gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-amber-800 dark:text-amber-200 text-sm">
            <strong className="font-semibold">Importante:</strong> {content.note}
          </p>
        </div>
      )}

      {content.image && (
        <>
          <div className="flex flex-col items-center my-6">
            <div
              className="relative group cursor-zoom-in max-w-lg"
              onClick={() => setImageModalOpen(true)}
              data-testid={testIdBase ? `${testIdBase}-image` : undefined}
            >
              <img
                src={content.image}
                alt={content.imageAlt || 'Imagen del manual'}
                className="rounded-xl border-2 border-secondary-200 dark:border-secondary-600 shadow-lg hover:shadow-xl transition-shadow duration-300"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-xl transition-colors duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 dark:bg-secondary-800/90 rounded-full p-3 shadow-lg">
                  <MagnifyingGlassIcon className="h-6 w-6 text-secondary-700 dark:text-secondary-300" />
                </div>
              </div>
            </div>
            {content.imageAlt && (
              <p className="mt-3 text-sm text-secondary-500 dark:text-secondary-400 italic text-center">
                {content.imageAlt}
              </p>
            )}
          </div>
          <ImageModal
            isOpen={imageModalOpen}
            onClose={() => setImageModalOpen(false)}
            imageSrc={content.image}
            imageAlt={content.imageAlt || 'Imagen del manual'}
            testIdBase={testIdBase}
          />
        </>
      )}
    </div>
  );
}

function ManualSectionCard({ section }: { section: ManualSection }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card className="overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid={`user-manual-section-toggle-${section.id}`}
        className="w-full bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5 text-left hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-white/20 rounded-lg">
              <div className="text-white">{section.icon}</div>
            </div>
            <h2 className="text-lg font-bold text-white">{section.title}</h2>
          </div>
          <div className={cn(
            "p-1 rounded-full bg-white/20 transition-transform duration-300",
            isOpen && "rotate-180"
          )}>
            <ChevronDownIcon className="h-5 w-5 text-white" />
          </div>
        </div>
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-500 ease-in-out',
          isOpen ? 'max-h-[10000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <CardBody className="p-6 lg:p-8 space-y-8 bg-white dark:bg-secondary-800">
          <div className="max-w-4xl mx-auto space-y-8">
            {section.content.map((content, index) => (
              <div key={index}>
                <ManualContentItem content={content} testIdBase={`user-manual-section-${section.id}-${index}`} />
                {index < section.content.length - 1 && (
                  <hr className="mt-8 border-secondary-200 dark:border-secondary-700" />
                )}
              </div>
            ))}
          </div>
        </CardBody>
      </div>
    </Card>
  );
}

export function UserManualPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSections = manualData.filter((section) => {
    const searchLower = searchTerm.toLowerCase();
    const titleMatch = section.title.toLowerCase().includes(searchLower);
    const contentMatch = section.content.some(
      (c) =>
        c.subtitle?.toLowerCase().includes(searchLower) ||
        c.text?.toLowerCase().includes(searchLower) ||
        c.list?.some((item) => item.toLowerCase().includes(searchLower))
    );
    return titleMatch || contentMatch;
  });

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          Manual de Usuario
        </h1>
        <p className="text-lg text-secondary-600 dark:text-secondary-400">
          Guía completa para el uso del sistema Facturación Electrónica
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-2xl mx-auto">
        <input
          type="text"
          placeholder="Buscar en el manual..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="user-manual-search-input"
          className="w-full px-5 py-4 pl-12 border-2 border-secondary-200 dark:border-secondary-600 rounded-xl bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all shadow-sm"
        />
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
      </div>

      {/* Manual Sections */}
      {filteredSections.length > 0 ? (
        <div className="space-y-6">
          {filteredSections.map((section) => (
            <div key={section.id} id={section.id} className="scroll-mt-4">
              <ManualSectionCard section={section} />
            </div>
          ))}
        </div>
      ) : (
        <Card className="shadow-md">
          <CardBody className="text-center py-16">
            <MagnifyingGlassIcon className="h-12 w-12 text-secondary-300 dark:text-secondary-600 mx-auto mb-4" />
            <p className="text-secondary-500 dark:text-secondary-400 text-lg">
              No se encontraron resultados para "<span className="font-medium">{searchTerm}</span>"
            </p>
            <p className="text-secondary-400 dark:text-secondary-500 text-sm mt-2">
              Intenta con otros términos de búsqueda
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
